import { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/joy";
import { useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

import { useAuthentication, useUserRoles, useUser } from "../../hooks/query";
import { useAuth } from "../../hooks";

export default function AuthHandler({ children }: { children: React.ReactNode }) {
    const { setIsAuthenticated, setUser, setToken, setRoles, isAuthenticated, token } = useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const location = useLocation();

    const { useTokenExchange } = useAuthentication();
    const tokenExchangeSvc = useTokenExchange();

    const { useListByUser } = useUserRoles();
    const listRoleByUserSvc = useListByUser();

    const { useGetUserById } = useUser();
    const getUserByIdSvc = useGetUserById();

    const fetchToken = async () => {
        // Skip token exchange on auth routes - let those components handle authentication
        const isAuthRoute = location.pathname.startsWith("/auth/");
        if (isAuthRoute) {
            setIsLoading(false);
            return;
        }

        // Skip if already authenticated (e.g., from callback) or if token already exists
        if (isAuthenticated || token) {
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);
            const tokenResponse = await tokenExchangeSvc.mutateAsync();
            const decoded = jwtDecode(tokenResponse) as any;
            const userId = decoded["username"];
            const listRoleByUserResponse = await listRoleByUserSvc.mutateAsync({ userId });
            const userResponse = await getUserByIdSvc.mutateAsync({ id: userId });

            setUser(userResponse);
            setRoles(listRoleByUserResponse);
            setToken(tokenResponse);
            setIsAuthenticated(true);
            setIsLoading(false);
        } catch (e) {
            console.error(e);
            setIsLoading(false);
        }
    };
    useEffect(() => {
        fetchToken().then();
    }, [location.pathname]);

    if (isLoading) {
        return (
            <Box
                display={"flex"}
                flexDirection={"column"}
                alignItems={"center"}
                justifyContent={"center"}
                minHeight={"100vh"}
                gap={0}
                textAlign={"center"}
            >
                <CircularProgress size="sm" variant="plain" sx={{ mb: 1 }} />
                <Typography level="title-lg">Signing you in…</Typography>
                <Typography level="body-sm" color="neutral">
                    Preparing your workspace
                </Typography>
            </Box>
        );
    }
    return <>{children}</>;
}
